import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  FiCheckCircle,
  FiCopy,
  FiDownload,
  FiRefreshCw,
  FiZap,
} from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

const API_BASE_URL =
  (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(/\/$/, "");

const TONES = [
  "Professional",
  "Formal",
  "Friendly",
  "Academic",
  "Persuasive",
  "Inspirational",
  "Confident",
  "Casual",
];

const LANGUAGES = [
  "English",
  "Bengali",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Hindi",
  "Arabic",
  "Chinese",
  "Japanese",
  "Korean",
];

const COLORS = {
  background: "#fff9f7",
  navy: "#1e2b49",
  orange: "#ff5a36",
  muted: "#61708a",
  border: "#dce3ec",
};

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.errors?.[0] ||
  (error.code === "ECONNABORTED"
    ? "The request timed out. Please try again."
    : error.message || "Could not generate a conclusion.");

const countWords = (value) => value.trim().match(/[^\s]+/g)?.length || 0;

export default function AIConclusionGenerator() {
  const [content, setContent] = useState("");
  const [tone, setTone] = useState("");
  const [language, setLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const inputWordCount = useMemo(() => countWords(content), [content]);

  const handleGenerate = async (event) => {
    event.preventDefault();
    const cleanContent = content.trim();

    if (!cleanContent) {
      setError("Please enter your topic or essay content.");
      toast.error("Topic or essay content is required.");
      return;
    }

    if (!tone) {
      setError("Please select a tone.");
      toast.error("Please select a tone.");
      return;
    }

    if (!language) {
      setError("Please select a language.");
      toast.error("Please select a language.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    console.log("[AI CONCLUSION UI] Sending generation request:", {
      endpoint: `${API_BASE_URL}/api/conclusion-generator`,
      contentCharacters: cleanContent.length,
      tone,
      language,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/conclusion-generator`,
        { content: cleanContent, tone, language },
        { timeout: 70000 }
      );

      const responseData = response.data?.data;

      if (!response.data?.success || !responseData?.conclusion?.trim()) {
        throw new Error("The server returned an empty conclusion.");
      }

      setResult(responseData);
      toast.success("Conclusion generated successfully!");
      console.log("[AI CONCLUSION UI] Generation completed:", {
        words: responseData.wordCount,
        characters: responseData.characterCount,
      });
    } catch (requestError) {
      const message = getErrorMessage(requestError);
      setError(message);
      toast.error(message);
      console.error("[AI CONCLUSION UI] Generation failed:", {
        message,
        status: requestError.response?.status,
        response: requestError.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.conclusion) return;

    try {
      await navigator.clipboard.writeText(result.conclusion);
      toast.success("Conclusion copied!");
    } catch (copyError) {
      console.error("[AI CONCLUSION UI] Copy failed:", copyError);
      toast.error("Could not copy the conclusion.");
    }
  };

  const handleDownload = () => {
    if (!result?.conclusion) return;

    const file = new Blob([result.conclusion], { type: "text/plain;charset=utf-8" });
    const fileUrl = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = fileUrl;
    anchor.download = "ai-conclusion.txt";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(fileUrl);
    toast.success("TXT file downloaded!");
  };

  const handleReset = () => {
    setContent("");
    setTone("");
    setLanguage("");
    setError("");
    setResult(null);
  };

  return (
    <main
      className="min-vh-100 py-5"
      style={{ backgroundColor: COLORS.background, color: COLORS.navy }}
    >
      <ToastContainer position="top-right" autoClose={3500} />

      <Container style={{ maxWidth: "1140px" }}>
       

        <Card
          className="border rounded-4 shadow-sm"
          style={{ borderColor: COLORS.border }}
        >
          <Card.Body className="p-4 p-md-5">
            <Form onSubmit={handleGenerate} noValidate>
              <Form.Group className="mb-4" controlId="conclusionContent">
                <Form.Label className="fw-semibold mb-2">
                  Topic / Essay Content <span style={{ color: COLORS.orange }}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  maxLength={2000}
                  value={content}
                  disabled={loading}
                  placeholder="Paste your essay or topic here..."
                  onChange={(event) => {
                    setContent(event.target.value);
                    if (error) setError("");
                  }}
                  style={{ minHeight: "160px", resize: "vertical" }}
                />
                <div className="d-flex justify-content-between mt-2 small" style={{ color: COLORS.muted }}>
                  <span>{inputWordCount.toLocaleString()} words</span>
                  <span>{content.length.toLocaleString()}/2,000 characters</span>
                </div>
              </Form.Group>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <Form.Group controlId="conclusionTone">
                    <Form.Label className="fw-semibold">
                      Tone <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={tone}
                      disabled={loading}
                      onChange={(event) => {
                        setTone(event.target.value);
                        if (error) setError("");
                      }}
                    >
                      <option value="">Select a tone...</option>
                      {TONES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="conclusionLanguage">
                    <Form.Label className="fw-semibold">
                      Language <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={language}
                      disabled={loading}
                      onChange={(event) => {
                        setLanguage(event.target.value);
                        if (error) setError("");
                      }}
                    >
                      <option value="">Select a language...</option>
                      {LANGUAGES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {error && (
                <Alert variant="danger" className="py-2" role="alert">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-100 border-0 rounded-pill fw-bold py-3 shadow-sm"
                style={{ backgroundColor: COLORS.orange }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generating Conclusion...
                  </>
                ) : (
                  <>
                    <FiZap className="me-2" aria-hidden="true" />
                    Generate Conclusion
                  </>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {result && (
          <Card className="border-0 rounded-4 shadow-sm mt-4 overflow-hidden">
            <Card.Header className="bg-white p-4 border-bottom d-flex flex-wrap gap-3 align-items-center justify-content-between">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <FiCheckCircle size={22} className="text-success" aria-hidden="true" />
                  <h2 className="h4 fw-bold mb-0">Generated Conclusion</h2>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <Badge bg="light" text="dark" className="border fw-normal">
                    {result.wordCount ?? countWords(result.conclusion)} words
                  </Badge>
                  <Badge bg="light" text="dark" className="border fw-normal">
                    {result.characterCount ?? result.conclusion.length} characters
                  </Badge>
                  <Badge bg="light" text="dark" className="border fw-normal">
                    {result.tone || tone}
                  </Badge>
                  <Badge bg="light" text="dark" className="border fw-normal">
                    {result.language || language}
                  </Badge>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={handleCopy}>
                  <FiCopy className="me-2" aria-hidden="true" />
                  Copy
                </Button>
                <Button variant="outline-secondary" onClick={handleDownload}>
                  <FiDownload className="me-2" aria-hidden="true" />
                  Download TXT
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-4 p-md-5">
              <div className="fs-5" style={{ color: "#34425b", lineHeight: 1.8 }}>
                {result.conclusion.split(/\n{2,}/).map((paragraph, index) => (
                  <p
                    key={`${paragraph.slice(0, 24)}-${index}`}
                    className={index === result.conclusion.split(/\n{2,}/).length - 1 ? "mb-0" : "mb-3"}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="d-flex justify-content-end mt-4">
                <Button variant="light" className="border" onClick={handleReset}>
                  <FiRefreshCw className="me-2" aria-hidden="true" />
                  Start Over
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </main>
  );
}
